// scheduling/server-timeline.ts — FE#447: anchor "is it due?" to the clock that MINTED the dueDate.
//
// A scheduled message's dueDate is minted from the CREATOR's clock (the picker's wall time → epoch on the
// creator's machine). When that clock differs from the server's, dueDate sits on the CREATOR's timeline.
// The SERVER records the creator's skew on the message at save (from the request's own clientNow) and is the
// only judge of "is it due yet?" (D − skew vs the server's clock). Clients keep NO clock model. A message
// created ELSEWHERE (web app, API, campaign, a server-computed recurring successor) carries no skew (0), so
// a skewed machine that merely SENDS it can never release it early.
//
// Convention, everywhere: skew = creatorClock − serverClock. Positive = the creator's clock runs ahead.
// Cross-runtime (Node + browser); no imports.

/** Below this the client/server difference is request latency + jitter, not a skewed clock. */
export const CREATOR_SKEW_NOISE_MS = 2 * 60 * 1000

/** Beyond this a clock is not believable: the widest real time-zone mix-up is ~26 h (UTC−12 … UTC+14);
 *  anything larger is a different bug (a wrong DATE) and the server clock is trusted instead. */
export const CREATOR_SKEW_MAX_MS = 26 * 60 * 60 * 1000

function toFiniteNumber(v: unknown): number | undefined {
    if (typeof v === 'number') return Number.isFinite(v) ? v : undefined
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v)
        return Number.isFinite(n) ? n : undefined
    }
    return undefined
}

/** The skew to STAMP on a message at save: client − server, or 0 when there is no believable
 *  client clock, the difference is noise, or it is beyond the cap. */
export function creatorSkewFromClientNow(serverNowMs: number, clientNowMs: unknown): number {
    const client = toFiniteNumber(clientNowMs)
    if (client === undefined) return 0
    const skew = client - serverNowMs
    const abs = Math.abs(skew)
    if (abs <= CREATOR_SKEW_NOISE_MS || abs > CREATOR_SKEW_MAX_MS) return 0
    return skew
}

/** A stored creator skew, read back safely: finite and within the cap, else 0. A corrupt or hostile
 *  stored value can therefore never move a send by more than the cap. */
export function boundedCreatorSkewMs(v: unknown): number {
    const n = toFiniteNumber(v)
    return n !== undefined && Math.abs(n) <= CREATOR_SKEW_MAX_MS ? n : 0
}
